import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../../app.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { generalConfigFromGlobal } from '../../shared/workspace-element-context';
import { ScreenVideoUploadService } from './screen-video-upload.service';
import { ScreenVideo } from '../screen-video-list/screen-video';

@Component({
    selector: 'gtw-screen-video-upload',
    templateUrl: './screen-video-upload.component.html',
    styleUrls: ['./screen-video-upload.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ScreenVideoUploadComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  inProgress = false;
  durationInSeconds = 5;

  private video!: HTMLVideoElement;
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D | null;

  private readonly subs = new Subscription();
  private selectedTimeSub?: Subscription;

  @ViewChild('posterFile')
  posterFile!: ElementRef<HTMLImageElement>;

  @ViewChild('videoFileInput')
  videoFileInput!: ElementRef<HTMLInputElement>;

  @Input('updateScreenVideo')
  set updateScreenVideoInput(video: ScreenVideo) {
    if (video && Object.keys(video).length > 0) {
      this.uploadScreenVideoForm = new UntypedFormGroup({
        videoId: new UntypedFormControl(video.video_id, [Validators.required]),
        videoName: new UntypedFormControl(video.video_name, [Validators.required]),
        videoDescription: new UntypedFormControl(video.video_desc, [Validators.required]),
        clientKey: new UntypedFormControl(this.clientKeyNum, [Validators.required]),
        screenKey: new UntypedFormControl(this.screenKeyNum, [Validators.required]),
        videoFile: new UntypedFormControl(''),
        posterFile: new UntypedFormControl(''),
        is_client_specific: new UntypedFormControl(video.is_client_specific == 'false' ? '' : 'true'),
      });
    }
    this.screenVideoFileName = '';
    this.canvas = this.renderer.createElement('video');
  }

  @Output()
  refreshScreenVideoList = new EventEmitter<boolean>();

  screenVideoFileName: string = '';
  selectedTime = 1;
  step = 1;
  selectedTimeChanged: Subject<number> = new Subject<number>();
  selectedVideoDuration = 0;
  progress = 75;

  uploadScreenVideoForm = new UntypedFormGroup({
    videoId: new UntypedFormControl(''),
    videoName: new UntypedFormControl('', [Validators.required]),
    videoDescription: new UntypedFormControl('', [Validators.required]),
    clientKey: new UntypedFormControl(0, [Validators.required]),
    screenKey: new UntypedFormControl(0, [Validators.required]),
    videoFile: new UntypedFormControl('', [Validators.required]),
    posterFile: new UntypedFormControl('', [Validators.required]),
    is_client_specific: new UntypedFormControl(false),
  });

  constructor(
    public gtwGlobal: GlobalService,
    public gtwWorkspace: WorkspaceService,
    public gtwApp: AppService,
    private changeDetectRef: ChangeDetectorRef,
    private screenVideoUploadService: ScreenVideoUploadService,
    private renderer: Renderer2,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.uploadScreenVideoForm.patchValue({
      clientKey: this.clientKeyNum,
      screenKey: this.screenKeyNum,
    });
  }

  private activeScreen(): any {
    return this.gtwWorkspace.workspaceFactory?.activeScreen || {};
  }

  private getBaseUrl(): string {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    return String(gc.base_url ?? gc.api ?? '');
  }

  get screenKeyNum(): number {
    const a = this.activeScreen();
    const raw = a.link ?? a.screenKey ?? a.screen_key;
    return parseInt(String(raw), 10);
  }

  get clientKeyNum(): number {
    const raw =
      this.gtwGlobal.global?.globalParams?.client_key ?? this.gtwApp.USER_SETTINGS?.user?.client_key;
    return parseInt(String(raw), 10);
  }

  get f() {
    return this.uploadScreenVideoForm.controls;
  }

  onFileSelected() {
    const inputFile: any = this.videoFileInput.nativeElement.files?.[0] || '';
    this.screenVideoFileName = inputFile.name;
    this.selectedTime = 1;
    this.extractFrames(inputFile);
    this.uploadScreenVideoForm.patchValue({
      videoFile: inputFile,
      screenKey: this.screenKeyNum,
      clientKey: this.clientKeyNum,
    });
    this.changeDetectRef.detectChanges();
  }

  selectFrame(value: number) {
    this.selectedTimeChanged.next(value);
  }

  private extractFrames(file: any) {
    // Tear down listeners + subscription from any prior file pick before
    // wiring up new ones, otherwise every additional file selection during
    // the same component lifetime stacks duplicate handlers on the
    // selectedTimeChanged Subject and on a fresh `<video>` element.
    this.detachVideoListeners();
    this.selectedTimeSub?.unsubscribe();

    this.video = this.renderer.createElement('video');
    this.canvas = this.renderer.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');

    this.video.muted = true;
    this.video.src = URL.createObjectURL(file);

    this.video.addEventListener('loadedmetadata', this.initCanvas, false);
    this.video.addEventListener('timeupdate', this.drawFrame, false);
    this.video.currentTime = 1;
    this.video.play();

    this.selectedTimeSub = this.selectedTimeChanged
      .pipe(
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe((value: any) => {
        this.video.currentTime = value;
        this.selectedTime = value;
        this.video.play();
      });
    this.subs.add(this.selectedTimeSub);
  }

  private detachVideoListeners(): void {
    if (!this.video) return;
    this.video.removeEventListener('loadedmetadata', this.initCanvas, false);
    this.video.removeEventListener('timeupdate', this.drawFrame, false);
  }

  ngOnDestroy(): void {
    this.detachVideoListeners();
    this.subs.unsubscribe();
  }

  private initCanvas = () => {
    this.canvas.width = this.video.videoWidth;
    this.canvas.height = this.video.videoHeight;
    this.selectedVideoDuration = Math.trunc(this.video.duration);
  };

  private drawFrame = () => {
    this.video.pause();
    this.ctx?.drawImage(this.video, 0, 0);

    this.canvas.toBlob(this.saveFrame, 'image/jpeg');
  };

  private saveFrame = (blob: any) => {
    this.posterFile.nativeElement.onload = this.revokeURL;
    this.posterFile.nativeElement.src = URL.createObjectURL(blob);
    this.uploadScreenVideoForm.patchValue({
      posterFile: blob,
    });
    this.changeDetectRef.detectChanges();
  };

  private revokeURL = () => {};

  uploadScreenVideo() {
    if (!this.uploadScreenVideoForm.valid) {
      return;
    }
    this.inProgress = true;
    const uploadFormData = new FormData();

    uploadFormData.append('videoId', this.uploadScreenVideoForm.get('videoId')?.value);
    uploadFormData.append('videoName', this.uploadScreenVideoForm.get('videoName')?.value);
    if (this.uploadScreenVideoForm.get('videoFile')?.value) {
      uploadFormData.append('file', this.uploadScreenVideoForm.get('videoFile')?.value);
    }
    uploadFormData.append('screenKey', this.uploadScreenVideoForm.get('screenKey')?.value);
    uploadFormData.append('clientKey', this.uploadScreenVideoForm.get('clientKey')?.value);
    uploadFormData.append('videoDesc', this.uploadScreenVideoForm.get('videoDescription')?.value);
    if (this.canvas.toDataURL && this.canvas) {
      uploadFormData.append('posterFile', this.canvas?.toDataURL('image/jpeg'));
    }
    const isCLientSpecific = this.uploadScreenVideoForm.get('is_client_specific')?.value;

    if (isCLientSpecific == true || isCLientSpecific == 'true') {
      uploadFormData.append('is_client_specific', 'true');
    } else {
      uploadFormData.append('is_client_specific', 'false');
    }

    this.screenVideoUploadService
      .upload(this.getBaseUrl(), uploadFormData, this.clientKeyNum).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.inProgress = false;

          if (res.callSuccess == 1 || parseInt(res.callSuccess) == 1) {
            this.openSnackBar('Video Uploaded Successfully !!');
            this.refreshScreenVideoList.emit(true);
            (document.querySelector('button.reset_button[mat-raised-button]') as HTMLElement).click();
          } else {
            this.openSnackBar('Video Upload Failed ' + res.errorMessage);
          }
        },
        (error) => {
          this.openSnackBar('Video Upload Failed');
          console.log('this is the error', error);
          this.inProgress = false;
        }
      );
  }

  resetUploadScreenVideoForm() {
    this.screenVideoFileName = '';
    this.uploadScreenVideoForm.get('videoId')?.clearValidators();
    this.uploadScreenVideoForm.get('videoFile')?.setValidators([Validators.required]);
    this.uploadScreenVideoForm.get('posterFile')?.setValidators([Validators.required]);
    this.uploadScreenVideoForm.patchValue({ is_client_specific: false, videoId: '' });
  }

  private openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: this.durationInSeconds * 1000,
    });
  }
}
